import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { BusinessHoursCard } from './BusinessHoursCard';
import { StaffShiftsCard } from './StaffShiftsCard';
import { ClosuresCard } from './ClosuresCard';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Building, Upload, Image as ImageIcon, MapPin, Loader2, Link, Copy, ExternalLink, Palette, Download } from 'lucide-react';
import { toast } from 'sonner';

const ITALIAN_PROVINCES = [
  'AG','AL','AN','AO','AP','AQ','AR','AT','AV','BA','BG','BI','BL','BN','BO','BR','BS','BT','BZ',
  'CA','CB','CE','CH','CL','CN','CO','CR','CS','CT','CZ','EN','FC','FE','FG','FI','FM','FR',
  'GE','GO','GR','IM','IS','KR','LC','LE','LI','LO','LT','LU','MB','MC','ME','MI','MN','MO','MS','MT',
  'NA','NO','NU','OR','PA','PC','PD','PE','PG','PI','PN','PO','PR','PT','PU','PV','PZ',
  'RA','RC','RE','RG','RI','RM','RN','RO','SA','SI','SO','SP','SR','SS','SU','SV',
  'TA','TE','TN','TO','TP','TR','TS','TV','UD','VA','VB','VC','VE','VI','VR','VT','VV'
];

const CompanyTab = ({ initialData }) => {
  const { API, getAuthHeaders } = useAuth();
  const [companyProfile, setCompanyProfile] = useState({
    ragione_sociale: initialData?.ragione_sociale || '',
    nome_brand: initialData?.nome_brand || '',
    partita_iva: initialData?.partita_iva || '',
    codice_fiscale: initialData?.codice_fiscale || '',
    indirizzo: initialData?.indirizzo || '',
    cap: initialData?.cap || '',
    citta: initialData?.citta || '',
    provincia: initialData?.provincia || '',
    telefono: initialData?.telefono || '',
    email: initialData?.email || '',
    logo_url: initialData?.logo_url || '',
    booking_slug: initialData?.booking_slug || '',
    booking_color: initialData?.booking_color || '#14B8A6',
    google_review_url: initialData?.google_review_url || ''
  });
  const [savingCompany, setSavingCompany] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [pullingLink, setPullingLink] = useState(false);

  const pullReviewLink = async () => {
    setPullingLink(true);
    try {
      const res = await axios.post(`${API}/google-business/review-link/pull`, {}, getAuthHeaders());
      handleChange('google_review_url', res.data.review_url);
      toast.success('Link preso da Google Business', { description: res.data.business_name || undefined });
    } catch (error) {
      toast.error('Non sono riuscito a prenderlo', {
        description: error.response?.data?.detail || error.message,
      });
    } finally {
      setPullingLink(false);
    }
  };

  const handleChange = (field, value) => {
    setCompanyProfile(prev => ({ ...prev, [field]: value }));
  };

  const saveCompanyProfile = async () => {
    setSavingCompany(true);
    try {
      await axios.post(`${API}/company-profile`, companyProfile, getAuthHeaders());
      alert('Profilo azienda salvato con successo!');
    } catch (error) {
      console.error('Errore salvataggio profilo azienda:', error);
      alert('Errore nel salvataggio del profilo azienda');
    } finally {
      setSavingCompany(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API}/company-profile/logo`, formData, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
      });
      setCompanyProfile(prev => ({ ...prev, logo_url: response.data.logo_url + '?t=' + Date.now() }));
      alert('Logo caricato con successo!');
    } catch (error) {
      console.error('Errore upload logo:', error);
      alert(error.response?.data?.detail || 'Errore nel caricamento del logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  return (
    <>
    <Card>
      <CardHeader>
        <CardTitle className="font-outfit flex items-center gap-2">
          <Building className="w-5 h-5" />
          Profilo Azienda
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Inserisci i dati della tua azienda per fatturazione e documenti
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Logo Upload */}
        <div className="space-y-3">
          <Label>Logo Aziendale</Label>
          <div className="flex items-center gap-4">
            <div className="w-24 h-24 rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center overflow-hidden bg-muted/30">
              {companyProfile.logo_url ? (
                <img src={`${API}${companyProfile.logo_url}`} alt="Logo" className="w-full h-full object-contain" />
              ) : (
                <ImageIcon className="w-8 h-8 text-muted-foreground/50" />
              )}
            </div>
            <div className="space-y-2">
              <input type="file" accept="image/*" onChange={uploadLogo} className="hidden" id="logo-upload" />
              <Button variant="outline" size="sm" onClick={() => document.getElementById('logo-upload').click()} disabled={uploadingLogo}>
                {uploadingLogo ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Carica Logo
              </Button>
              <p className="text-xs text-muted-foreground">JPG, PNG o WebP. Max 5MB</p>
            </div>
          </div>
        </div>

        <Separator />

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="ragione_sociale">Ragione Sociale *</Label>
            <Input id="ragione_sociale" value={companyProfile.ragione_sociale} onChange={(e) => handleChange('ragione_sociale', e.target.value)} placeholder="Es. Salone Bellezza S.r.l." />
          </div>
          <div className="space-y-2">
            <Label htmlFor="nome_brand">Nome del Brand</Label>
            <Input id="nome_brand" value={companyProfile.nome_brand} onChange={(e) => handleChange('nome_brand', e.target.value)} placeholder="Es. Beauty Center" />
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="partita_iva">Partita IVA</Label>
            <Input id="partita_iva" value={companyProfile.partita_iva} onChange={(e) => handleChange('partita_iva', e.target.value)} placeholder="IT12345678901" maxLength={13} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="codice_fiscale">Codice Fiscale</Label>
            <Input id="codice_fiscale" value={companyProfile.codice_fiscale} onChange={(e) => handleChange('codice_fiscale', e.target.value.toUpperCase())} placeholder="RSSMRA80A01H501U" maxLength={16} />
          </div>
        </div>

        <Separator />

        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-muted-foreground" />
            <Label className="text-base font-medium">Indirizzo</Label>
          </div>
          <div className="space-y-2">
            <Label htmlFor="indirizzo">Via e Numero Civico</Label>
            <Input id="indirizzo" value={companyProfile.indirizzo} onChange={(e) => handleChange('indirizzo', e.target.value)} placeholder="Via Roma, 123" />
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="cap">CAP</Label>
              <Input id="cap" value={companyProfile.cap} onChange={(e) => handleChange('cap', e.target.value)} placeholder="00100" maxLength={5} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="citta">Città</Label>
              <Input id="citta" value={companyProfile.citta} onChange={(e) => handleChange('citta', e.target.value)} placeholder="Roma" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="provincia">Provincia</Label>
              <Select value={companyProfile.provincia} onValueChange={(value) => handleChange('provincia', value)}>
                <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
                <SelectContent>
                  {ITALIAN_PROVINCES.map((prov) => (
                    <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <Separator />

        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="telefono">Numero di Telefono</Label>
            <Input id="telefono" type="tel" value={companyProfile.telefono} onChange={(e) => handleChange('telefono', e.target.value)} placeholder="+39 06 1234567" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="company_email">E-mail Aziendale</Label>
            <Input id="company_email" type="email" value={companyProfile.email} onChange={(e) => handleChange('email', e.target.value)} placeholder="info@azienda.it" />
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <Button onClick={saveCompanyProfile} disabled={savingCompany}>
            {savingCompany ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Salvataggio...</>) : 'Salva Profilo Azienda'}
          </Button>
        </div>
      </CardContent>
    </Card>

    {/* Booking Link Section */}
    <Card>
      <CardHeader>
        <CardTitle className="font-outfit flex items-center gap-2">
          <Link className="w-5 h-5" />Link Prenotazione Online
        </CardTitle>
        <p className="text-sm text-muted-foreground">
          Configura il link pubblico che i tuoi clienti useranno per prenotare autonomamente
        </p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2">
          <Label htmlFor="booking_slug">Nome nel Link (slug)</Label>
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="flex items-center px-3 py-2 bg-muted rounded-md sm:rounded-l-md sm:rounded-r-none border sm:border-r-0 text-xs sm:text-sm text-muted-foreground break-all sm:whitespace-nowrap">
              {window.location.origin}/prenota/
            </div>
            <Input
              id="booking_slug"
              value={companyProfile.booking_slug}
              onChange={(e) => handleChange('booking_slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-'))}
              placeholder="nome-salone"
              className="sm:rounded-l-none"
              data-testid="booking-slug-input"
            />
          </div>
          <p className="text-xs text-muted-foreground">Solo lettere minuscole, numeri e trattini. Es: beauty-center-roma</p>
        </div>

        <div className="space-y-2">
          <Label className="flex items-center gap-2"><Palette className="w-4 h-4" />Colore Primario Pagina Prenotazione</Label>
          <div className="flex items-center gap-3">
            <input
              type="color"
              value={companyProfile.booking_color}
              onChange={(e) => handleChange('booking_color', e.target.value)}
              className="w-10 h-10 rounded cursor-pointer border border-border"
            />
            <Input
              value={companyProfile.booking_color}
              onChange={(e) => handleChange('booking_color', e.target.value)}
              className="w-32 font-mono text-sm"
              data-testid="booking-color-input"
            />
            <div className="h-8 flex-1 rounded-md" style={{ backgroundColor: companyProfile.booking_color }} />
          </div>
        </div>

        {companyProfile.booking_slug && (
          <div className="p-4 rounded-lg bg-muted/50 border space-y-3">
            <Label className="text-xs text-muted-foreground uppercase tracking-wider">Il tuo link di prenotazione</Label>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-sm font-medium bg-card px-3 py-2 rounded border truncate" data-testid="booking-link-display">
                {window.location.origin}/prenota/{companyProfile.booking_slug}
              </code>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/prenota/${companyProfile.booking_slug}`);
                  alert('Link copiato!');
                }}
                data-testid="copy-booking-link-btn"
              >
                <Copy className="w-4 h-4 mr-1" />Copia
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => window.open(`/prenota/${companyProfile.booking_slug}`, '_blank')}
                data-testid="open-booking-link-btn"
              >
                <ExternalLink className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-muted-foreground">Condividi questo link sui tuoi social, biglietti da visita o sito web.</p>
          </div>
        )}

        {/* Google Review Link */}
        <div className="space-y-2 pt-4 border-t">
          <Label htmlFor="google_review_url" className="flex items-center gap-2">
            ⭐ Link Recensioni Google
          </Label>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              id="google_review_url"
              value={companyProfile.google_review_url}
              onChange={(e) => handleChange('google_review_url', e.target.value)}
              placeholder="https://g.page/r/CYxxxxxxxxxxxx/review"
              data-testid="google-review-url-input"
            />
            <Button type="button" variant="outline" onClick={pullReviewLink} disabled={pullingLink}
              className="min-h-[44px] shrink-0 w-full sm:w-auto" data-testid="pull-review-link-btn">
              {pullingLink
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Lo cerco...</>
                : <><Download className="w-4 h-4 mr-2" />Prendi da Google Business</>}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Lo prendiamo dall'attività che gestisci in <a href="/google-business" className="text-primary hover:underline">Google Business</a>, oppure incollalo a mano dal tuo pannello <a href="https://business.google.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Google Business</a>. Finisce nei messaggi di follow-up (WhatsApp, email e SMS).
          </p>
        </div>

        <div className="flex justify-end">
          <Button onClick={saveCompanyProfile} disabled={savingCompany}>
            {savingCompany ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Salvataggio...</>) : 'Salva Impostazioni'}
          </Button>
        </div>
      </CardContent>
    </Card>

    <BusinessHoursCard />
    <StaffShiftsCard />
    <ClosuresCard />
  </>
  );};

export default CompanyTab;
